import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Clock3,
  MapPin,
  Search,
  SlidersHorizontal,
  Star,
} from 'lucide-react';
import api from '../api';
import { useLanguage } from '../LanguageContext';
import { LOCATION_TREE, locationLabel, splitLocation } from '../data/locations';
import useServiceCategories from '../data/useServiceCategories';
import './Marketplace.css';

const PAGE_SIZE = 8;
const DELIVERY_OPTIONS = [
  { value: '24h', zh: '24 小时内', en: 'Within 24 hours' },
  { value: '3d', zh: '3 天内', en: 'Within 3 days' },
  { value: '7d', zh: '一周内', en: 'Within a week' },
];
const removeFavorite = (targetType, targetId) => api.delete(`/favorites/${targetType}/${targetId}`);

function formatPostedAt(value, language) {
  if (!value) return language === 'zh' ? '最近发布' : 'Recently posted';
  const date = new Date(String(value).replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return language === 'zh' ? '最近发布' : 'Recently posted';
  return date.toLocaleDateString(language === 'zh' ? 'zh-CN' : 'en-US', { month: 'short', day: 'numeric' });
}

function parseCategories(value) {
  return String(value || '')
    .split(',')
    .map(item => item.trim())
    .filter(item => item && item !== '全部' && item !== 'all');
}

function selectedLocationParts(value) {
  return splitLocation(value);
}

export default function Services() {
  const { language, copy, localizeCategory, localizeMethod } = useLanguage();
  const serviceCategories = useServiceCategories();
  const texts = copy.services;
  const [searchParams, setSearchParams] = useSearchParams();
  const [liveServices, setLiveServices] = useState([]);
  const [liveMeta, setLiveMeta] = useState({ total: 0, page: 1, limit: PAGE_SIZE });
  const [favoriteServiceIds, setFavoriteServiceIds] = useState([]);
  const [searchInput, setSearchInput] = useState(searchParams.get('keyword') || '');

  const selectedCategories = parseCategories(searchParams.get('categories') || searchParams.get('category'));
  const location = searchParams.get('location') || '';
  const method = searchParams.get('method') || '全部';
  const keyword = searchParams.get('keyword') || '';
  const priceMin = searchParams.get('priceMin') || '';
  const priceMax = searchParams.get('priceMax') || '';
  const delivery = searchParams.get('delivery') || 'all';
  const savedMode = searchParams.get('saved') === '1';
  const sort = searchParams.get('sort') || 'best';
  const page = Math.max(1, Number(searchParams.get('page') || 1));
  const tab = savedMode ? 'saved' : (sort === 'latest' ? 'recent' : 'best');
  const selectedCategoryParam = selectedCategories.join(',');
  const [loading, setLoading] = useState(true);

  const text = language === 'zh' ? {
    location: '位置',
    allLocations: '全部位置',
    categories: '分类',
    priceRange: '价格区间（$）',
    min: '最低',
    max: '最高',
    delivery: '交付时间',
    reset: '重置筛选',
    resultCount: count => `${count} 项服务`,
    sortBy: '排序',
    recommended: '推荐',
    highestRated: '评分最高',
    lowestPrice: '价格从低到高',
    highestPrice: '价格从高到低',
    fastest: '最快交付',
    all: '全部',
    searchPlaceholder: language === 'zh' ? '搜索服务标题、分类或服务者' : 'Search service, category, or provider',
    search: language === 'zh' ? '搜索' : 'Search',
    previous: language === 'zh' ? '上一页' : 'Previous',
    next: language === 'zh' ? '下一页' : 'Next',
    remote: language === 'zh' ? '远程' : 'Remote',
    view: language === 'zh' ? '查看服务' : 'View service',
    noSaved: language === 'zh' ? '还没有收藏服务。' : 'No saved services yet.',
    noResults: language === 'zh' ? '没有找到符合条件的服务。' : 'No matching services found.',
    loading: language === 'zh' ? '正在加载服务...' : 'Loading services...',
    provider: language === 'zh' ? '服务者' : 'Provider',
    orders: language === 'zh' ? '单' : 'orders',
  } : {
    location: 'Location',
    allLocations: 'All locations',
    categories: 'Categories',
    priceRange: 'Price range ($)',
    min: 'Min',
    max: 'Max',
    delivery: 'Delivery time',
    reset: 'Reset filters',
    resultCount: count => `${count} services`,
    sortBy: 'Sort by',
    recommended: 'Recommended',
    highestRated: 'Highest rated',
    lowestPrice: 'Price: low to high',
    highestPrice: 'Price: high to low',
    fastest: 'Fastest delivery',
    all: 'All',
    searchPlaceholder: 'Search service, category, or provider',
    search: 'Search',
    previous: 'Previous',
    next: 'Next',
    remote: 'Remote',
    view: 'View service',
    noSaved: 'No saved services yet.',
    noResults: 'No matching services found.',
    loading: 'Loading services...',
    provider: 'Provider',
    orders: 'orders',
  };

  const updateParams = updates => {
    const params = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === '' || value === null || value === undefined || value === 'all' || value === '全部') params.delete(key);
      else params.set(key, String(value));
    });
    if (!Object.prototype.hasOwnProperty.call(updates, 'page')) params.delete('page');
    setSearchParams(params);
  };

  const updateTab = nextTab => {
    if (nextTab === 'saved') {
      updateParams({ saved: '1', sort: 'best', page: '' });
      return;
    }
    updateParams({ saved: '', sort: nextTab === 'recent' ? 'latest' : 'best', page: '' });
  };

  const resetFilters = () => {
    const params = new URLSearchParams(searchParams);
    ['categories', 'category', 'method', 'location', 'priceMin', 'priceMax', 'delivery', 'page'].forEach(key => params.delete(key));
    setSearchParams(params);
  };

  const goPage = nextPage => {
    const next = Math.max(1, Math.min(totalPages, nextPage));
    updateParams({ page: next === 1 ? '' : next });
  };

  const toggleCategory = category => {
    const next = selectedCategories.includes(category)
      ? selectedCategories.filter(item => item !== category)
      : [...selectedCategories, category];
    updateParams({ categories: next.join(','), category: '' });
  };

  const updateLocationPart = (index, value) => {
    const parts = selectedLocationParts(location);
    parts[index] = value;
    if (index === 0) {
      parts[1] = '';
      parts[2] = '';
    } else if (index === 1) {
      parts[2] = '';
    }
    updateParams({ location: parts.filter(Boolean).join(':') });
  };

  const handleSearch = event => {
    event.preventDefault();
    updateParams({ keyword: searchInput.trim() });
  };

  useEffect(() => {
    setSearchInput(keyword);
  }, [keyword]);

  useEffect(() => {
    const requestPage = savedMode ? 1 : page;
    setLoading(true);
    api.get('/marketplace/services', {
      params: {
        keyword,
        categories: selectedCategoryParam,
        method,
        location,
        priceMin,
        priceMax,
        delivery,
        sort,
        page: requestPage,
        limit: savedMode ? 50 : PAGE_SIZE,
      },
    })
      .then(res => {
        const servicesData = res.data.services || [];
        setLiveServices(servicesData.map(item => ({ ...item, id: `listing-${item.id}`, liveId: item.id })));
        setLiveMeta({
          total: Number(res.data.total ?? servicesData.length),
          page: Number(res.data.page || requestPage),
          limit: Number(res.data.limit || PAGE_SIZE),
        });
      })
      .catch(() => {
        setLiveServices([]);
        setLiveMeta({ total: 0, page: 1, limit: PAGE_SIZE });
      })
      .finally(() => setLoading(false));
  }, [keyword, selectedCategoryParam, method, location, priceMin, priceMax, delivery, sort, page, savedMode]);

  useEffect(() => {
    api.get('/favorites', { params: { targetType: 'service' } })
      .then(res => {
        const ids = (res.data.favorites || []).map(item => Number(item.target_id));
        setFavoriteServiceIds(ids);
      })
      .catch(() => setFavoriteServiceIds([]));
  }, []);

  const savedServices = useMemo(
    () => liveServices.filter(service => service.liveId && favoriteServiceIds.includes(Number(service.liveId))),
    [favoriteServiceIds, liveServices]
  );
  const visibleServices = savedMode
    ? savedServices.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
    : liveServices;
  const totalCount = savedMode ? savedServices.length : Number(liveMeta.total || 0);
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const country = LOCATION_TREE.find(item => item.value === selectedLocationParts(location)[0]);
  const city = country?.cities.find(item => item.value === selectedLocationParts(location)[1]);
  const serviceCategoryOptions = serviceCategories.map(category => category.name);

  const toggleSaved = async service => {
    if (!service.liveId) return;
    const targetId = Number(service.liveId);
    const isSaved = favoriteServiceIds.includes(targetId);
    try {
      if (isSaved) {
        await removeFavorite('service', targetId);
        setFavoriteServiceIds(prev => prev.filter(item => item !== targetId));
      } else {
        await api.post('/favorites', { targetType: 'service', targetId });
        setFavoriteServiceIds(prev => Array.from(new Set([...prev, targetId])));
      }
    } catch {
      // Keep the current state when the API rejects a favorite action.
    }
  };

  const hasProviderVerification = service => (
    service.providerVerificationStatus === 'verified' ||
    (Array.isArray(service.verified) && service.verified.length > 0)
  );

  return (
    <div className="uw-page">
      <div className="container uw-browse-container">
        <header className="uw-browse-header">
          <div>
            <span className="uw-browse-kicker">{texts.findWork}</span>
            <h1>{texts.title}</h1>
            <p>{texts.subtitle}</p>
          </div>
          <span className="uw-location-summary"><MapPin size={16} /> {locationLabel(location, language)}</span>
        </header>

        <form className="uw-browse-search" onSubmit={handleSearch}>
          <Search size={19} aria-hidden="true" />
          <input value={searchInput} onChange={event => setSearchInput(event.target.value)} placeholder={text.searchPlaceholder} />
          <button type="submit">{text.search}</button>
        </form>

        <div className="uw-browse-layout">
          <aside className="uw-filter-sidebar" aria-label={texts.filters}>
            <div className="uw-filter-title"><SlidersHorizontal size={17} /> <strong>{texts.filters}</strong></div>

            <fieldset className="uw-filter-group">
              <legend>{text.location}</legend>
              <div className="uw-location-grid">
                <select value={selectedLocationParts(location)[0]} onChange={event => updateLocationPart(0, event.target.value)} aria-label={text.location}>
                  <option value="">{text.allLocations}</option>
                  {LOCATION_TREE.map(item => <option key={item.value} value={item.value}>{item.label[language] || item.value}</option>)}
                </select>
                <select value={selectedLocationParts(location)[1]} onChange={event => updateLocationPart(1, event.target.value)} disabled={!country} aria-label={language === 'zh' ? '城市' : 'City'}>
                  <option value="">{language === 'zh' ? '全部城市' : 'All cities'}</option>
                  {country?.cities.map(item => <option key={item.value} value={item.value}>{item.label[language] || item.value}</option>)}
                </select>
                <select value={selectedLocationParts(location)[2]} onChange={event => updateLocationPart(2, event.target.value)} disabled={!city} aria-label={language === 'zh' ? '区域' : 'District'}>
                  <option value="">{language === 'zh' ? '全部区域' : 'All districts'}</option>
                  {city?.districts.map(item => <option key={item.value} value={item.value}>{item.label[language] || item.value}</option>)}
                </select>
              </div>
            </fieldset>

            <fieldset className="uw-filter-group">
              <legend>{text.categories}</legend>
              <div className="uw-filter-checks">
                {serviceCategoryOptions.map(categoryOption => (
                  <label key={categoryOption}>
                    <input type="checkbox" checked={selectedCategories.includes(categoryOption)} onChange={() => toggleCategory(categoryOption)} />
                    <span>{localizeCategory(categoryOption)}</span>
                  </label>
                ))}
              </div>
            </fieldset>

            <fieldset className="uw-filter-group">
              <legend>{text.priceRange}</legend>
              <div className="uw-price-range">
                <label><span>$</span><input type="number" min="0" value={priceMin} onChange={event => updateParams({ priceMin: event.target.value })} placeholder={text.min} /></label>
                <span>–</span>
                <label><span>$</span><input type="number" min="0" value={priceMax} onChange={event => updateParams({ priceMax: event.target.value })} placeholder={text.max} /></label>
              </div>
            </fieldset>

            <fieldset className="uw-filter-group">
              <legend>{text.delivery}</legend>
              <div className="uw-filter-checks">
                {DELIVERY_OPTIONS.map(option => (
                  <label key={option.value}>
                    <input type="radio" name="delivery" checked={delivery === option.value} onChange={() => updateParams({ delivery: option.value })} />
                    <span>{option[language]}</span>
                  </label>
                ))}
              </div>
            </fieldset>

            <button className="uw-reset-button" type="button" onClick={resetFilters}>{text.reset}</button>
          </aside>

          <main className="uw-feed">
            <div className="uw-tabs">
              <button className={tab === 'best' ? 'active' : ''} type="button" onClick={() => updateTab('best')}>{texts.best}</button>
              <button className={tab === 'recent' ? 'active' : ''} type="button" onClick={() => updateTab('recent')}>{texts.recent}</button>
              <button className={tab === 'saved' ? 'active' : ''} type="button" onClick={() => updateTab('saved')}>{texts.saved}</button>
            </div>

            <div className="uw-feed-stats">
              <div>
                <span>{texts.availableServices}</span>
                <strong>{totalCount}</strong>
              </div>
              <div>
                <span>{texts.savedServices}</span>
                <strong>{savedServices.length}</strong>
              </div>
            </div>

            <div className="uw-results-toolbar">
              <strong>{text.resultCount(totalCount)}</strong>
              <label>
                <span>{text.sortBy}</span>
                <select value={sort} onChange={event => updateParams({ sort: event.target.value })}>
                  <option value="best">{text.recommended}</option>
                  <option value="rating">{text.highestRated}</option>
                  <option value="price_asc">{text.lowestPrice}</option>
                  <option value="price_desc">{text.highestPrice}</option>
                  <option value="fastest">{text.fastest}</option>
                </select>
              </label>
            </div>

            {loading ? (
              <div className="uw-empty-state">{text.loading}</div>
            ) : (
              <section className="uw-job-list">
                {visibleServices.map(service => {
                  const saved = favoriteServiceIds.includes(Number(service.liveId));
                  return (
                    <article key={service.id} className="uw-job-card">
                      <div className="uw-job-top">
                        <div>
                          <Link to={`/services/${service.id}`} className="uw-job-title">{service.title}</Link>
                          <p className="uw-provider-line">
                            {text.provider}: {service.provider || (language === 'zh' ? '服务者' : 'Provider')}
                            {' · '}
                            {localizeCategory(service.category)}
                            {' · '}
                            {formatPostedAt(service.created_at, language)}
                          </p>
                        </div>
                        <button className={`uw-save ${saved ? 'saved' : ''}`} type="button" aria-label={saved ? texts.saved : texts.saveService} onClick={() => toggleSaved(service)}>
                          {saved ? '♥' : '♡'}
                        </button>
                      </div>
                      <p className="uw-job-desc">{service.description || service.summary}</p>
                      <div className="uw-job-tags">
                        {(service.tags || []).map(tag => <span key={tag}>{tag}</span>)}
                      </div>
                      <div className="uw-job-meta">
                        {hasProviderVerification(service) && <span className="uw-verified">{texts.verified}</span>}
                        <span><Star size={14} fill="currentColor" /> {service.rating || 0} {texts.rating}</span>
                        <span>{service.orders || 0} {text.orders}</span>
                        <span><Clock3 size={14} /> {service.availability || text.fastest}</span>
                        <span>{service.method === '远程' ? text.remote : localizeMethod(service.method)}</span>
                      </div>
                      <div className="uw-job-bottom">
                        <span className="uw-job-price">{service.price || '—'}</span>
                        <Link to={`/services/${service.id}`}>{text.view} <ArrowRight size={15} /></Link>
                      </div>
                    </article>
                  );
                })}
                {visibleServices.length === 0 && <div className="uw-empty-state">{savedMode ? text.noSaved : text.noResults}</div>}
              </section>
            )}

            {totalPages > 1 && (
              <nav className="uw-pagination" aria-label={language === 'zh' ? '服务分页' : 'Service pagination'}>
                <button type="button" onClick={() => goPage(page - 1)} disabled={page <= 1}><ArrowLeft size={15} /> {text.previous}</button>
                <span>{page} / {totalPages}</span>
                <button type="button" onClick={() => goPage(page + 1)} disabled={page >= totalPages}>{text.next} <ArrowRight size={15} /></button>
              </nav>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
